import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
// @statsig/expo-bindings re-exports everything from @statsig/react-bindings (including
// useStatsigUser) -- imported from here, matching PantriesScreen.jsx's own useFeatureGate/
// useStatsigClient imports, since @statsig/react-bindings itself isn't a direct dependency.
import { useStatsigUser } from '@statsig/expo-bindings';

// StatsigProviderComponent (StatsigProviderExpo) constructs its client from a useState
// initializer, so the `user` prop below only ever seeds the FIRST render -- it is not reactive.
// The signed-in user's numeric id is populated later, asynchronously (getCurrentUser, no
// redux-persist), so without this every session would be evaluated as the anonymous stableID
// user for its entire lifetime. Rendered as a child of StatsigProviderComponent (rather than
// inside AppStatsigUser itself) so useStatsigUser() has a real StatsigContext to read from.
function StatsigUserSync({ userId, children }) {
  const { updateUserSync } = useStatsigUser();
  // The client is already seeded with the mount-time userId via AppStatsigUser's `user` prop, so
  // the first effect run has nothing new to apply -- only a later change (e.g. login completing
  // asynchronously) needs to push an update.
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    updateUserSync(userId ? { userID: String(userId) } : {});
  }, [userId, updateUserSync]);

  return children;
}

// Rendered inside <Provider> (App itself can't call useSelector -- Provider doesn't exist yet at
// App's own render time) so the Statsig user context can reflect a logged-in user instead of
// always being anonymous. Only the stable numeric userID is passed, never email/name -- PII must
// always be masked, per org policy.
// Named export matches the rest of this codebase's hook/component convention (e.g.
// components/map/useSiteSelection.ts, SiteMap.tsx).
// eslint-disable-next-line import/prefer-default-export
export function AppStatsigUser({ StatsigProviderComponent, sdkKey, options, children }) {
  const userId = useSelector((state) => state.registration.user?.id);
  const user = userId ? { userID: String(userId) } : {};

  return (
    <StatsigProviderComponent sdkKey={sdkKey} user={user} options={options}>
      <StatsigUserSync userId={userId}>{children}</StatsigUserSync>
    </StatsigProviderComponent>
  );
}
